import React, { createContext, useContext, useId, useMemo } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

interface FormFieldContextType {
  id: string;
  name?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  size?: ComponentSize;
}

const FormFieldContext = createContext<FormFieldContextType | null>(null);

export function useFormField() {
  return useContext(FormFieldContext);
}

type FormFieldSpacing = 'none' | 'compact' | 'default' | 'spacious';

interface FormFieldProps extends ForwardedHTMLProps {
  children: React.ReactNode;
  name?: string;
  error?: boolean | string;
  disabled?: boolean;
  required?: boolean;
  size?: ComponentSize;
  spacing?: FormFieldSpacing;
  horizontal?: boolean;
  className?: string;
}

const spacingClasses: Record<FormFieldSpacing, string> = {
  none: '',
  compact: 'space-y-1',
  default: 'space-y-1.5',
  spacious: 'space-y-2',
};

export const FormField = React.forwardRef<HTMLFieldSetElement, FormFieldProps>(
  (
    {
      children,
      name,
      error = false,
      disabled = false,
      required = false,
      size = 'md',
      spacing = 'default',
      horizontal = false,
      className,
      ...props
    },
    ref
  ) => {
    const forwardedProps = forwardProps(props);
    const uniqueId = useId();
    const fieldId = name || uniqueId;

    const contextValue = useMemo(
      () => ({
        id: fieldId,
        name,
        error: !!error,
        disabled,
        required,
        size,
      }),
      [fieldId, name, error, disabled, required, size]
    );

    return (
      <FormFieldContext.Provider value={contextValue}>
        <fieldset
          ref={ref}
          disabled={disabled}
          className={cn(
            'border-none p-0 m-0',
            horizontal ? 'flex items-start gap-4' : cn('flex flex-col', spacingClasses[spacing]),
            disabled && 'opacity-60',
            className
          )}
          aria-invalid={!!error}
          aria-describedby={error && typeof error === 'string' ? `${fieldId}-error` : undefined}
          {...forwardedProps}
          {...props}
        >
          {children}
          {typeof error === 'string' && error && (
            <span id={`${fieldId}-error`} className="text-sm text-red-500 dark:text-red-400" role="alert">
              {error}
            </span>
          )}
        </fieldset>
      </FormFieldContext.Provider>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
